"use client";
import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { City } from './interface';
import API from '../app/api/index';
interface CityContextType {
  cities: City[];
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  getCityById: (id: string) => City | undefined;
  getCityByName: (name: string) => City | undefined;
  getCityByFormattedName: (formattedName: string) => City | undefined;
}

const CityContext = createContext<CityContextType | undefined>(undefined);

export const useCityContext = () => {
  const context = useContext(CityContext);
  if (context === undefined) {
    throw new Error('useCityContext must be used within a CityProvider');
  }
  return context;
};

interface CityProviderProps {
  children: ReactNode;
}

export const CityProvider: React.FC<CityProviderProps> = ({ children }) => {
  const [cities, setCities] = useState<City[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Start with loading state`
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Ensure we're on the client before making API calls
  useEffect(() => {
    setIsClient(true);
  }, []);

  const fetchCities = useCallback(async () => {
    // If already initialized, don't fetch again
    if (isInitialized) {
      return;
    }
    // isLoading is already true from initial state
    setError(null);
    // Add timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setCities([]);
      setIsLoading(false);
      setIsInitialized(true);
      setError('Request timed out');
    }, 10000); // 10 second timeout

    try {
      const response = await API.user.GetCities();
      // Clear timeout on successful response
      clearTimeout(timeoutId);
      
      if (API.validators.checkSuccessCode(response)) {
        const citiesData: City[] = response.data?.data || [];
        setCities(citiesData);
        setError(null);
      } else {
        setError('Failed to load cities');
        // Add fallback cities if API fails
        const fallbackCities: City[] = [
          { _id: 'fallback-2', name: 'Delhi' },
          { _id: 'fallback-3', name: 'Bangalore' },
        ];
        setCities(fallbackCities);
      }
    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);
      console.error('Error fetching cities:', error);
      setError('Error loading cities');
      // Add fallback cities if API fails
      const fallbackCities: City[] = [
        { _id: 'fallback-1', name: 'Mumbai' },
        { _id: 'fallback-2', name: 'Delhi' },
        { _id: 'fallback-3', name: 'Bangalore' },
      ];
      setCities(fallbackCities);
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, [isInitialized]); // Remove isLoading from dependencies to prevent circular dependency

  useEffect(() => {
    if (isClient) {
      fetchCities();
    }
  }, [isClient, fetchCities]);

  const getCityById = (id: string): City | undefined => {
    return cities.find(city => city._id === id);
  };

  const getCityByName = (name: string): City | undefined => {
    return cities.find(city => city.name.toLowerCase() === name.toLowerCase());
  };

  const getCityByFormattedName = (formattedName: string): City | undefined => {
    return cities.find(city => {
      const formattedCityName = city.name.toLowerCase().replace(/\s+/g, '-');
      return formattedCityName === formattedName;
    });
  };

  const value: CityContextType = {
    cities,
    isLoading,
    error,
    isInitialized,
    getCityById,
    getCityByName,
    getCityByFormattedName,
  };

  return (
    <CityContext.Provider value={value}>
      {children}
    </CityContext.Provider>
  );
}; 